/**
 * Analytics utilities for PostHog integration
 * Centralized tracking events for the Ramps application
 *
 * This module defines three main event categories:
 * 1. ENTRY: Where users came from (wallet partner, referrer, etc.)
 * 2. JOURNEY: What users are doing (page views, interactions, form submissions)
 * 3. EXIT: Where users exit from (completion, cancellation, abandonment)
 *
 * For helper utilities (error tracking, privacy controls, performance tracking),
 * see analytics-helpers.ts
 */

import { extractClientNameFromJwt } from "./jwt-decoder";

// Re-export helper utilities for convenience
export {
	AnalyticsPrivacy,
	BatchedEventTracker,
	PerformanceTimer,
	trackApiCall,
	trackError,
	trackPerformance,
	trackValidationError,
} from "./analytics-helpers";

// Re-export profile tracking utilities
export {
	getProfileStatus,
	setProfileProperties,
	trackProfileCreation,
	trackProfileLookup,
	trackProfileMilestone,
	trackProfileUpdate,
	trackReturningUser,
	trackWalletAssociation,
} from "./profile-analytics";

export interface PartnerAttribution {
	wallet_provider: string;
	wallet_provider_raw: string | undefined;
	wallet_provider_known: boolean;
}

/**
 * Shape a partner name is allowed to take once trimmed and lowercased.
 *
 * This is a bound on untrusted input, not a partner allowlist: the value can
 * originate in a JWT payload that is deliberately not signature-verified
 * client-side, and it ends up as a PostHog group key. Every real partner name
 * (uglycash, decaf, meru, beansapp, via, …) fits comfortably.
 */
const PARTNER_NAME_PATTERN = /^[a-z0-9][a-z0-9 ._-]*$/;
const PARTNER_NAME_MAX_LENGTH = 64;

/**
 * Normalize a wallet_provider value for analytics.
 *
 * Partners are not validated against an allowlist: new partners are onboarded
 * continuously and an allowlist silently reclassifies them as unknown, which is
 * what caused wallet_provider_known to be false on effectively every event.
 * `wallet_provider_known` therefore means "we resolved an actual value", not
 * "this partner is on a list we maintain".
 *
 * Values are lowercased and trimmed so they join with historical events, and are
 * otherwise passed through verbatim as long as they look like a partner name.
 * Anything malformed becomes "unknown" — but `wallet_provider_raw` still carries
 * the original, so rejected values stay visible in PostHog rather than vanishing.
 */
export function normalizeWalletProvider(
	raw: string | undefined | null,
): PartnerAttribution {
	const normalized = raw?.trim().toLowerCase() || "";
	const isValid =
		normalized.length <= PARTNER_NAME_MAX_LENGTH &&
		PARTNER_NAME_PATTERN.test(normalized);

	return {
		wallet_provider: isValid ? normalized : "unknown",
		wallet_provider_raw: raw || undefined,
		wallet_provider_known: isValid,
	};
}

/**
 * Resolve the partner for analytics attribution.
 *
 * The JWT is the source of truth: its `client_name` claim is present on the URL
 * at mount for every v3 session, so attribution is available synchronously
 * before the first event fires. The `/transactions/prepare` response and the
 * `wallet_provider` URL param are fallbacks for flows without a JWT (v2/v5) or
 * tokens minted without the claim.
 *
 * Only `client_name` is read from the token — no subject or other claim — and
 * the signature is deliberately not verified: this is attribution metadata, not
 * authorization.
 */
export function resolvePartner(input: {
	token?: string | null;
	prepared?: string | null;
	urlParam?: string | null;
}): PartnerAttribution {
	// "unknown" is a sentinel written by earlier code paths, not a partner name.
	const prepared =
		input.prepared && input.prepared !== "unknown" ? input.prepared : null;

	return normalizeWalletProvider(
		(input.token ? extractClientNameFromJwt(input.token) : null) ||
			prepared ||
			input.urlParam ||
			null,
	);
}

/**
 * Analytics Events - Following naming convention: {subject}_{action}
 */
export const ANALYTICS_EVENTS = {
	// ============================================
	// ENTRY EVENTS - Tracking where users came from
	// ============================================
	SESSION_STARTED: "session_started",
	APP_INITIALIZED: "app_initialized",

	// ============================================
	// JOURNEY EVENTS - Page Views
	// ============================================
	PAGE_CALCULATOR_VIEWED: "page_calculator_viewed",
	PAGE_PHONE_VIEWED: "page_phone_viewed",
	PAGE_NAME_VIEWED: "page_name_viewed",
	PAGE_ADDRESS_VIEWED: "page_address_viewed",
	PAGE_DOB_VIEWED: "page_dob_viewed",
	PAGE_IDENTIFICATION_VIEWED: "page_identification_viewed",
	PAGE_DIGITAL_OFFRAMP_VIEWED: "page_digital_offramp_viewed",
	PAGE_ADDITIONAL_INFO_VIEWED: "page_additional_info_viewed",
	PAGE_REVIEW_VIEWED: "page_review_viewed",
	PAGE_STATUS_VIEWED: "page_status_viewed",
	PAGE_CANCEL_VIEWED: "page_cancel_viewed",
	PAGE_CANCEL_SUCCESS_VIEWED: "page_cancel_success_viewed",
	PAGE_TRANSACTION_STATUS_VIEWED: "page_transaction_status_viewed",
	PAGE_MAINTENANCE_VIEWED: "page_maintenance_viewed",
	PAGE_CORE_API_ERROR_VIEWED: "page_core_api_error_viewed",
	// Status page displayed for a transaction held for manual review
	TRANSACTION_HOLD_VIEWED: "transaction_hold_viewed",

	// ============================================
	// JOURNEY EVENTS - User Actions
	// ============================================
	// Calculator actions (A/B experiment events)
	CALCULATOR_PAGE_VIEWED: "calculator_page_viewed",
	CALCULATOR_CONTINUE_CLICKED: "calculator_continue_clicked",
	AMOUNT_CHANGED: "amount_changed",
	SERVICE_OPTION_SELECTED: "service_option_selected",
	LOCATION_SEARCHED: "location_searched",
	LOCATION_SELECTED: "location_selected",

	// Form interactions
	FORM_FIELD_COMPLETED: "form_field_completed",
	FORM_FIELD_EDITED: "form_field_edited",
	FORM_SUBMITTED: "form_submitted",

	// Validation events
	VALIDATION_FAILED: "validation_failed",
	VALIDATION_SUCCESS: "validation_success",

	// Review page actions
	TERMS_VIEWED: "terms_viewed",
	TRANSACTION_INITIATED: "transaction_initiated",
	RAMPS_UI_FLOW_COMPLETED: "ramps_ui_flow_completed",
	POSTMESSAGE_SENT: "postmessage_sent",
	LEGACY_POSTMESSAGE_SENT: "legacy_postmessage_sent",

	// Embedded wallet send events (post-commit interception)
	EMBEDDED_INIT_RECEIVED: "embedded_init_received",
	EMBEDDED_COMMIT_REQUESTED: "embedded_commit_requested",
	EMBEDDED_COMMIT_CONFIRMED: "embedded_commit_confirmed",
	EMBEDDED_COMMIT_FAILED: "embedded_commit_failed",

	// Location services
	LOCATION_PERMISSION_GRANTED: "location_permission_granted",
	LOCATION_PERMISSION_DENIED: "location_permission_denied",
	NEARBY_LOCATIONS_LOADED: "nearby_locations_loaded",
	STATUS_SEE_ALL_LOCATIONS_CLICKED: "status_see_all_locations_clicked",

	// ============================================
	// EXIT EVENTS - Tracking where users exit
	// ============================================
	TRANSACTION_COMPLETED: "transaction_completed",
	TRANSACTION_CANCELLED: "transaction_cancelled",
	TRANSACTION_CANCEL_FAILED: "transaction_cancel_failed",
	FUNDS_DEPOSITED: "funds_deposited",
	FUNDS_COLLECTED: "funds_collected",
	SESSION_ABANDONED: "session_abandoned",
	ERROR_OCCURRED: "error_occurred",
} as const;

export type AnalyticsEvent =
	(typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Base properties included in ALL events
 * Automatically enriched by the analytics hook
 */
export interface BaseAnalyticsProperties {
	// Environment context
	environment?: string; // d5, q5, ext, prod
	api_version?: string; // v2 or v5

	// Session context
	session_id?: string;
	timestamp?: number;

	// User journey context
	page_path?: string;
	current_step?: string;
	wallet_provider?: string; // Resolved partner (JWT client_name, prepare, or URL param)
	wallet_provider_known?: boolean; // Whether an actual provider value was resolved
	operation?: "withdraw" | "deposit"; // Cash-in or cash-out flow

	// Device context
	user_agent?: string;
	language?: string;

	// Referrer context (entry tracking)
	referrer?: string;
	initial_landing_page?: string;
}

/**
 * Entry Event Properties - Tracking where users came from
 */
export interface EntryEventProperties extends BaseAnalyticsProperties {
	// Entry source
	entry_source?: "direct" | "referral" | "embedded" | "webview";
	wallet_provider: string; // REQUIRED - Which wallet partner

	// URL parameters that indicate entry context
	has_token?: boolean;
	has_transaction_id?: boolean;
	operation?: "withdraw" | "deposit";
	asset_code?: string;

	// WebView detection
	is_webview?: boolean;
	webview_platform?: "ios" | "android" | "unknown";
}

/**
 * Page View Event Properties
 */
export interface PageViewProperties extends BaseAnalyticsProperties {
	page_name: string;
	page_title?: string;
	previous_page?: string;
	time_on_previous_page_ms?: number;
}

/**
 * Calculator-specific analytics properties
 */
export interface CalculatorAnalyticsProperties extends BaseAnalyticsProperties {
	amount?: number;
	source_currency?: string;
	destination_currency?: string;
	service_option?: "WILL_CALL" | "BANK_DEPOSIT" | "DIRECT_TO_ACCT" | null;
	location_id?: string;
	location_name?: string;
	estimated_fee?: number;
	exchange_rate?: number;
}

/**
 * Calculator UI Experiment properties
 * Used for A/B testing the new calculator UI
 */
export interface CalculatorExperimentProperties
	extends CalculatorAnalyticsProperties {
	// Feature flag context for experiment attribution
	"$feature/ramps-new-calculator-page-enabled"?: string | null;
	experiment_variant?: "control" | "test" | null;
}

/**
 * Form-specific analytics properties
 */
export interface FormAnalyticsProperties extends BaseAnalyticsProperties {
	form_step?: string;
	form_type?: string;
	field_name?: string;
	field_type?: string;
	validation_error?: string;
	completion_time_ms?: number;
	is_editing?: boolean; // True if editing from review page
}

/**
 * Transaction-specific analytics properties
 */
export interface TransactionAnalyticsProperties
	extends BaseAnalyticsProperties {
	transaction_id?: string;
	transaction_type?: string;
	transaction_state?: string;
	amount?: number;
	currency?: string;
	source_currency?: string;
	destination_currency?: string;
	service_option?: string | null;
	status?: string;
	error_code?: string;
	error_message?: string;
	payment_method?: "cash" | "bank";
}

/**
 * Standalone amount capture properties — fired at calculator continue
 * to record the finalized amount before the commit stage.
 */
export interface TransactionAmountProperties extends BaseAnalyticsProperties {
	transaction_amount: number;
	source_currency: string;
	destination_currency: string;
	service_option?: string | null;
	exchange_rate?: number;
	estimated_fee?: number;
	transaction_id?: string;
}

/**
 * Embedded Wallet Event Properties - Post-commit interception tracking
 * Tracks postMessage events between the Ramps UI and the parent wallet app
 */
export interface EmbeddedWalletEventProperties extends BaseAnalyticsProperties {
	transaction_id?: string;
	amount?: number | string;
	asset_code?: string; // e.g. "USDC", "EURC"
	wallet_provider?: string; // e.g. "lobstr", "xbull"
	message_type?: string; // the postMessage type sent (e.g. "COMMIT_REQUEST", "INIT_TRANSACTION")
	message_channel?: "react_native" | "ios_webkit" | "iframe" | "unknown";
	has_crypto_instructions?: boolean;
	reference_number?: string;
	error_message?: string;
}

/**
 * Exit Event Properties - Tracking where/why users exit
 */
export interface ExitEventProperties extends BaseAnalyticsProperties {
	exit_type: "completed" | "cancelled" | "abandoned" | "error";
	exit_reason?: string;
	last_page?: string;
	abandonment_step?: string;
	pages_visited?: number;
	time_in_session_ms?: number;
	completion_percentage?: number; // 0-100
	transaction_id?: string;
	transaction_status?: string;
	amount?: number;
	source_currency?: string;
	destination_currency?: string;
	service_option?: string;
}

/**
 * Validation Event Properties
 */
export interface ValidationEventProperties extends BaseAnalyticsProperties {
	validation_type?:
		| "phone"
		| "name"
		| "address"
		| "id"
		| "bank"
		| "transaction";
	validation_step?: string;
	error_fields?: string[]; // List of fields that failed
	error_count?: number;
	retry_attempt?: number;
}

/**
 * Location Event Properties
 */
export interface LocationEventProperties extends BaseAnalyticsProperties {
	permission_status?: "granted" | "denied" | "prompt";
	location_accuracy?: number;
	locations_found?: number;
	search_radius_km?: number;
	selected_location_id?: string;
	selected_location_name?: string;
	distance_to_location_km?: number;
}

/**
 * Error Event Properties
 */
export interface ErrorEventProperties extends BaseAnalyticsProperties {
	error_type?: "api" | "network" | "validation" | "system";
	error_code?: string;
	error_message?: string;
	error_stack?: string;
	api_endpoint?: string;
	http_status?: number;
	recovery_action?: string;
}
